import { Component, defineElement } from '@theme/component';

export class YisePurchaseOptions extends Component {
  ready() {
    if (!this.isConnected || !this.abortController || this.abortController.signal.aborted) return false;
    if (this.initialized) return true;
    if (!this.dataset.variantId
      || !['true', 'false'].includes(this.dataset.defaultSubscribe)
      || !this.hasAttribute('data-selected-selling-plan-id')
      || !this.querySelector(':scope > template[data-purchase-options-ready]')) {
      this.waitForMarkup();
      return false;
    }
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.initialized = true;
    if (this._selectedSellingPlanId === undefined) {
      this._selectedSellingPlanId = this.dataset.selectedSellingPlanId || null;
    }

    this.listen(this, 'change', (event) => {
      if (!this.controlsReady) return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.checked) return;
      if (input.matches('[data-purchase-mode="onetime"]')) {
        this.selectSellingPlan(null);
      } else if (input.matches('[data-purchase-mode="subscribe"]')) {
        if (this.firstSellingPlanId) this.selectSellingPlan(this.firstSellingPlanId);
      } else if (input.matches('[data-purchase-frequency]')) {
        this.selectSellingPlan(input.value);
      }
    });

    this.listen(this, 'click', (event) => {
      if (!this.controlsReady) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-subscription-card]') || target.closest('[data-subscription-frequency]')) return;
      if (this.selectedSellingPlanId === null && this.firstSellingPlanId) {
        this.selectSellingPlan(this.firstSellingPlanId);
      }
    });
    const productForm = this.closest('yise-product-form');
    if (productForm) this.listen(productForm, 'yise:product-form-ready', () => this.releasePendingControls());
    this.syncSelection();
    this.dispatchEvent(new CustomEvent('yise:purchase-options-ready', { bubbles: true }));
    return true;
  }

  waitForMarkup() {
    if (this.markupObserver || typeof MutationObserver === 'undefined') return;
    const connection = this.abortController;
    this.markupObserver = new MutationObserver(() => {
      if (this.abortController !== connection || connection.signal.aborted || !this.isConnected) return;
      this.ready();
    });
    this.markupObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-variant-id', 'data-default-subscribe', 'data-selected-selling-plan-id'],
    });
  }

  teardown() {
    this.initialized = false;
    this.widget?.querySelectorAll('[data-purchase-mode], [data-purchase-frequency]').forEach((control) => {
      if (control.closest('yise-purchase-options') !== this) return;
      control.disabled = true;
      control.setAttribute('data-pending-purchase-control', '');
    });
    this.markupObserver?.disconnect();
    this.markupObserver = null;
  }

  get widget() {
    return this.querySelector(':scope > [data-subscription-widget]');
  }

  get controlsReady() {
    const productForm = this.closest('yise-product-form');
    return Boolean(this.initialized && (!productForm || productForm.formControlsReady));
  }

  get firstSellingPlanId() {
    return this.variantState?.sellingPlanAllocations?.[0]?.id
      ? String(this.variantState.sellingPlanAllocations[0].id)
      : this.widget?.dataset.firstSellingPlanId || null;
  }

  get selectedSellingPlanId() {
    return this._selectedSellingPlanId === undefined
      ? this.dataset.selectedSellingPlanId || null
      : this._selectedSellingPlanId;
  }

  set selectedSellingPlanId(value) {
    this._selectedSellingPlanId = value === null || value === undefined || value === ''
      ? null
      : String(value);
    this.dataset.selectedSellingPlanId = this._selectedSellingPlanId || '';
    this.syncSelection();
  }

  selectSellingPlan(value) {
    const previousSellingPlanId = this.selectedSellingPlanId;
    this.selectedSellingPlanId = value;
    if (previousSellingPlanId === this.selectedSellingPlanId) return;
    this.dispatchEvent(new CustomEvent('yise:purchase-option-change', {
      bubbles: true,
      detail: {
        selectedSellingPlanId: this.selectedSellingPlanId,
        previousSellingPlanId,
        source: 'user',
      },
    }));
  }

  syncVariant(variant) {
    if (!this.initialized) return false;
    this.variantState = variant || null;
    const variantId = variant?.id === undefined || variant?.id === null ? '' : String(variant.id);
    if (variantId !== this.dataset.variantId) {
      const template = [...this.querySelectorAll(':scope > template[data-purchase-variant-template]')]
        .find((candidate) => candidate.dataset.purchaseVariantTemplate === variantId);
      const nextWidget = template?.content.firstElementChild?.cloneNode(true) || null;
      const currentWidget = this.widget;
      if (currentWidget && nextWidget) currentWidget.replaceWith(nextWidget);
      else if (currentWidget) currentWidget.remove();
      else if (nextWidget) this.prepend(nextWidget);
      this.dataset.variantId = variantId;
    }

    if (this.selectedSellingPlanId === null && this.dataset.defaultSubscribe === 'true') {
      const firstPlan = variant?.sellingPlanAllocations?.[0];
      if (firstPlan) this._selectedSellingPlanId = String(firstPlan.id);
    }
    this.dataset.selectedSellingPlanId = this.selectedSellingPlanId || '';
    this.syncSelection();
    return true;
  }

  syncSelection() {
    if (!this.initialized) return;
    const widget = this.widget;
    if (!widget) return;
    const selected = this.selectedSellingPlanId;
    const oneTime = widget.querySelector('[data-purchase-mode="onetime"]');
    const subscribe = widget.querySelector('[data-purchase-mode="subscribe"]');
    if (oneTime) oneTime.checked = selected === null;
    if (subscribe) subscribe.checked = selected !== null;
    for (const input of widget.querySelectorAll('[data-purchase-frequency]')) {
      input.checked = input.value === selected;
      input.nextElementSibling?.classList.toggle('subscription-frequency__button--selected', input.checked);
    }
    const sellingPlanInput = widget.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) sellingPlanInput.value = selected || '';
    this.releasePendingControls();
  }

  releasePendingControls() {
    if (!this.controlsReady) return;
    this.widget?.querySelectorAll('[data-pending-purchase-control]').forEach((control) => {
      if (control.closest('yise-purchase-options') !== this) return;
      control.disabled = false;
      control.removeAttribute('data-pending-purchase-control');
    });
  }

  syncPricing({ baseVariantPrice, subscriptionPrice, title, benefits, banner, hideFrequencyDiscount }) {
    if (!this.initialized) return;
    const widget = this.widget;
    if (!widget) return;
    this.setPriceHtml(widget.querySelector('[data-subscription-base-price]'), baseVariantPrice);
    const subscribePrice = widget.querySelector('[data-subscription-price]');
    if (subscribePrice) {
      subscribePrice.hidden = !subscriptionPrice;
      this.setPriceHtml(subscribePrice, subscriptionPrice);
    }
    const titleElement = widget.querySelector('[data-subscription-title]');
    if (titleElement && titleElement.textContent !== String(title || '')) {
      titleElement.textContent = title || '';
    }
    const benefitsElement = widget.querySelector('[data-subscription-benefits]');
    if (benefitsElement) {
      let bannerElement = benefitsElement.querySelector('.subscription-benefits__banner');
      if (!banner) bannerElement?.remove();
      else {
        if (!bannerElement) {
          bannerElement = document.createElement('div');
          bannerElement.className = 'subscription-benefits__banner';
          benefitsElement.prepend(bannerElement);
        }
        if (bannerElement.textContent !== banner) bannerElement.textContent = banner;
      }
      const existingBenefits = [...benefitsElement.querySelectorAll('.subscription-benefit')];
      const nextBenefits = Array.isArray(benefits) ? benefits : [];
      nextBenefits.forEach((benefit, index) => {
        let benefitElement = existingBenefits[index];
        if (!benefitElement) {
          benefitElement = document.createElement('div');
          benefitElement.className = 'subscription-benefit';
          benefitsElement.append(benefitElement);
        }
        const text = '✓ ' + benefit;
        if (benefitElement.textContent !== text) benefitElement.textContent = text;
      });
      existingBenefits.slice(nextBenefits.length).forEach((element) => element.remove());
    }
    widget.querySelector('[data-subscription-frequency]')?.classList.toggle(
      'subscription-frequency--no-discount', Boolean(hideFrequencyDiscount),
    );
  }

  setPriceHtml(element, value) {
    if (!element) return;
    const html = String(value || '').replace('.00', '');
    if (element.innerHTML !== html) element.innerHTML = html;
  }
}

defineElement('yise-purchase-options', YisePurchaseOptions);
